import { useTracking } from "react-tracking";
import { AnalyticsInstance } from "analytics";
import { AnalyticsEvent, AnalyticsEventType } from "../types";

export interface UseKubraTrackingParams {
  commonTrackingData?: Partial<AnalyticsEvent>;
  analytics?: AnalyticsInstance;
  onPageView?: (data: any) => void;
  onUserIdentity?: (data: any) => void;
  onEvent?: (data: any) => void;
}

interface EventProcessingParams {
  analytics?: AnalyticsInstance;
  eventData: Partial<AnalyticsEvent>;
  onEventCallback?: (data: any) => void;
}

function processPageViewEvent({ analytics, eventData, onEventCallback }: EventProcessingParams) {
  analytics?.page(eventData, undefined, onEventCallback).catch((err) => console.error(err));
}

function processUserIdentityEvent({
  analytics,
  eventData,
  onEventCallback,
}: EventProcessingParams) {
  const { userId, ...identityData } = eventData;

  if (!userId) {
    throw new Error("userId field is required for 'userIdentity' events");
  }

  analytics
    ?.identify(userId, identityData, undefined, onEventCallback)
    .catch((err: any) => console.error(err));
}

function processCustomEvent({ analytics, eventData, onEventCallback }: EventProcessingParams) {
  const { event, ...customEventData } = eventData;

  if (!event) {
    throw new Error("event field is required for 'event' events");
  }

  analytics
    ?.track(event, customEventData, undefined, onEventCallback)
    .catch((err) => console.error(err));
}

function kubraDispatch(
  data: AnalyticsEvent,
  analytics?: AnalyticsInstance,
  onPageView?: (data: any) => void,
  onUserIdentity?: (data: any) => void,
  onEvent?: (data: any) => void
) {
  const { type, ...eventData } = data;

  switch (type) {
    case AnalyticsEventType.pageView:
      processPageViewEvent({ analytics, eventData, onEventCallback: onPageView });
      break;
    case AnalyticsEventType.userIdentity:
      processUserIdentityEvent({ analytics, eventData, onEventCallback: onUserIdentity });
      break;
    case AnalyticsEventType.customEvent:
      processCustomEvent({ analytics, eventData, onEventCallback: onEvent });
      break;
    default:
      throw new Error(`${type} is not a valid analytics event type.`);
  }
}

export const useKubraTracking = ({
  commonTrackingData,
  analytics,
  onPageView,
  onUserIdentity,
  onEvent,
}: UseKubraTrackingParams = {}) => {
  return {
    ...useTracking<AnalyticsEvent>(commonTrackingData, {
      dispatch: (data: AnalyticsEvent) =>
        kubraDispatch(data, analytics, onPageView, onUserIdentity, onEvent),
    }),
  };
};
